import { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NodeStore, { FunctionListItem } from '../../stores/NodeStore';
import BaseNode, { NodeInput, NodeOutput, contentStyle as style } from "../node-types/base-nodes/BaseNode";
import { Connection, Handle, Position } from "reactflow";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import InputOutputNode, { Data } from '../node-types/base-nodes/InputOutputNode';
import { optionNode, captureResponseNode, emailNode, imageNode, promptNode, questionNode, yesNoNode } from '../../config/node-types';
import useDesigner from '../../hooks/useDesigner';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import { useTheme } from '@mui/material/styles';
import { rootNodeTypes } from '../../config/config-designer';
import TextField from '@mui/material/TextField';
import FunctionsIcon from '@mui/icons-material/Functions';

type Props = {
    data: {
        value: string
    },
    isConnectable: boolean,
    id: string
}

const config = {
    name: 'Add Node',
    color: "#1411F5",
    nodeType: 'searchNodesNode',
    inputs: [],
    outputs: []
} as Data


function AddNodePopup({ data, isConnectable, id }: Props) {
    const { nodes } = useDesigner();
    const theme = useTheme();
    const ref1 = useRef<HTMLDivElement>(null);
    const { removeNode, replaceNodeWithType, functions } = useDesigner();
    const [hasSelected, setHasSelected] = useState(false);
    const [value, setValue] = useState('');

    useEffect(() => {
        let timer1 = setTimeout(() => {
            if (ref1.current != null) {
                ref1.current.focus()
            }
        }, 10);

        // this will clear Timeout
        // when component unmount like in willComponentUnmount
        // and show will not change to true
        return () => {
            clearTimeout(timer1);
        };

    }, [])

    const filteredFunctions = useMemo(() => {
        if (value.trim() === '') return functions;

        return functions.filter(s => s.name.toUpperCase().includes(value.toUpperCase()));
    }, [functions, value])

    const filteredRootNodes = useMemo(() => {
        if (value.trim() === '') return rootNodeTypes;

        return rootNodeTypes.filter(s => s.label.toUpperCase().includes(value.toUpperCase()));
    }, [value])

    const onChange = useCallback((evt: ChangeEvent<HTMLTextAreaElement>) => {
        NodeStore.update((s, o) => {
            const temp = structuredClone(o.chat);

            const node = temp.nodes.find(s => s.id === id);

            if (node) {
                node.data.value = evt.target.value;
            }
            localStorage.setItem('nodes', JSON.stringify(temp));
            s.chat = temp;
            s.isDirty = true;
        });

        setHasSelected(true);
    }, []);

    //

    const handleSelect = (nodeType: string) => {
        replaceNodeWithType(id, nodeType, '00000000-0000-0000-0000-000000000000', false, '')
    }

    const openFunction = (func: FunctionListItem) => {
        replaceNodeWithType(id, 'functionNode', func.id, true, func.name)
    }

    return (
        <BaseNode
            hideHeading={false}
            label={config.name}
            selected={true}
            color={config.color}
            content={
                <>
                    <div>
                        <TextField
                            placeholder='Search'
                            size="small"
                            ref={ref1}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            sx={{
                                input: { color: 'black' },
                                "& .MuiInputBase-root.MuiOutlinedInput-root ::placeholder": {
                                    color: "black"
                                }
                            }}
                        />
                    </div>
                    <div
                        tabIndex={-1}
                        onBlur={() => { /*if (!hasSelected) removeNode(id) */ }}
                        ref={ref1}
                        style={{ minWidth: 250, display: 'flex', flexDirection: 'column', maxHeight: 150, overflowY: 'auto' }}
                    >
                        {/*  <textarea placeholder='Enter an image upload prompt' rows={6} cols={30} name="text" onChange={onChange} className="nodrag" value={data.value} style={{ border: '1px solid lightgray;', borderRadius: 5, margin: 5, fontSize: 16, fontFamily: 'sans-serif', minWidth: 200 }} />*/}

                        {
                            filteredRootNodes.map(nt =>
                                <div
                                    onClick={() => handleSelect(nt.type)}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: 5,
                                        padding: 5,
                                        color: theme.palette.secondary.main,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {nt.icon}
                                    {nt.label}
                                </div>
                            )
                        }

                        {
                            filteredFunctions.map(ft =>
                                <div
                                    onClick={() => openFunction(ft)}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: 5,
                                        padding: 5,
                                        color: theme.palette.secondary.main,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FunctionsIcon />
                                    {ft.name}
                                </div>
                            )
                        }
                    </div>

                    <div
                        style={{ ...style.io }}
                    >
                        <Handle
                            type="target"
                            position={Position.Left}
                            id={'inp_playNode'}
                            style={{ ...style.handleInvisible, backgroundColor: 'white' }}
                            isValidConnection={(connection: Connection) => {
                                const destNode = nodes.find(s => s.id === connection.source);

                                if (!destNode) return false;

                                return connection.sourceHandle === "src_playNode" && connection.targetHandle === "inp_playNode";
                            }}
                        >
                        </Handle>
                    </div>
                </>
            }
        />
    );
}

export default AddNodePopup;